import React, { useState, useEffect, useRef } from 'react';
import MessageList from './MessageList';
import InputBox from './InputBox';
import { chatService } from '../services/agentService';
import { sessionService } from '../services/sessionService';

export default function ChatInterface({ sessionId, onNewMessage, onSessionIdUpdate }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [retryCountdown, setRetryCountdown] = useState(null);
  const [pendingRetryMessage, setPendingRetryMessage] = useState(null);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const retryTimerRef = useRef(null);

  // Cleanup retry timer on unmount
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
    };
  }, []);

  // Handle retry countdown
  useEffect(() => {
    if (retryCountdown === null) {
      return;
    }

      if (retryCountdown > 0) {
      // Update the retry message with current countdown
      setMessages(prev => {
        const updated = prev.map(msg => {
          if (msg.role === 'assistant' && msg.content && msg.content.includes('I am going to retry in')) {
            // Extract attempt number if present, otherwise default to attempt 1
            const attemptMatch = msg.content.match(/attempt (\d+) of 3/);
            const attemptNum = attemptMatch ? attemptMatch[1] : '1';
            return {
              ...msg,
              content: `I am going to retry in ${retryCountdown} seconds... (attempt ${attemptNum} of 3)`
            };
          }
          return msg;
        });
        sessionService.saveMessages(sessionId, updated);
        return updated;
      });

      const timer = setTimeout(() => {
        setRetryCountdown(retryCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Countdown reached 0, retry the message
      if (pendingRetryMessage && sessionId) {
        // Retry the message
        const retryFunction = async () => {
          setLoading(true);
          
          // Get current retry attempt count
          const currentAttempt = retryAttempt;
          console.log(`Retrying message (attempt ${currentAttempt + 1} of 3)...`);
          
          try {
            const response = await chatService.sendMessage(sessionId, pendingRetryMessage);
            
            // Check if PDF is available
            if (response.pdf_available) {
              // PDF is available - show message with PDF icon
              const pdfMessage = {
                role: 'assistant',
                content: response.response || 'Your campaign plan PDF is ready!',
                timestamp: new Date().toISOString(),
                pdf_blob_url: response.pdf_blob_url,
                pdf_filename: response.pdf_filename
              };
              
              setMessages(prev => {
                const filtered = prev.filter(msg => 
                  !(msg.role === 'assistant' && msg.content && msg.content.includes('I am going to retry in'))
                );
                const next = [...filtered, pdfMessage];
                sessionService.saveMessages(sessionId, next);
                return next;
              });
              
              // Reset retry state on success
              setRetryAttempt(0);
              setPendingRetryMessage(null);
              setRetryCountdown(null);
              
              if (onNewMessage) {
                onNewMessage();
              }
              setLoading(false);
              return;
            }
            
            // Check if retry also failed with throttling
            if (response.error || (response.response && response.response.includes('encountered an error'))) {
              const errorMessageText = response.response || '';
              const isThrottlingError = errorMessageText.includes('throttlingException') || 
                                        errorMessageText.includes('throttling') ||
                                        errorMessageText.includes('request rate is too high');
              
              if (isThrottlingError && currentAttempt < 2) {
                // Still throttling, retry again (we have 3 total attempts: initial + 2 retries)
                const nextAttempt = currentAttempt + 1;
                console.log(`Retry attempt ${currentAttempt + 1} failed with throttling, will retry again (attempt ${nextAttempt + 1} of 3)...`);
                setRetryAttempt(nextAttempt);
                setRetryCountdown(30);
                
                const retryMessage = {
                  role: 'assistant',
                  content: `I am going to retry in 30 seconds... (attempt ${nextAttempt + 1} of 3)`,
                  timestamp: new Date().toISOString(),
                  isRetryMessage: true
                };
                
                setMessages(prev => {
                  // Update existing retry message or add new one
                  const filtered = prev.filter(msg => 
                    !(msg.role === 'assistant' && msg.content && msg.content.includes('I am going to retry in'))
                  );
                  const next = [...filtered, retryMessage];
                  sessionService.saveMessages(sessionId, next);
                  return next;
                });
                setLoading(false);
                return;
              }
            }
            
            // Success or non-throttling error
            if (response.session_id && response.session_id !== sessionId) {
              console.warn('Backend returned different session ID:', sessionId, '->', response.session_id);
            }
            
            const assistantMessage = {
              role: 'assistant',
              content: response.response || 'Sorry, I encountered an error. Please try again.',
              timestamp: new Date().toISOString()
            };
            
            setMessages(prev => {
              // Remove retry message
              const filtered = prev.filter(msg => 
                !(msg.role === 'assistant' && msg.content && msg.content.includes('I am going to retry in'))
              );
              const next = [...filtered, assistantMessage];
              sessionService.saveMessages(sessionId, next);
              return next;
            });
            
            // Reset retry state on success
            setRetryAttempt(0);
            setPendingRetryMessage(null);
            setRetryCountdown(null);
            
            if (onNewMessage) {
              onNewMessage();
            }
          } catch (error) {
            console.error('Error on retry:', error);
            const errorMessageText = error?.response?.data?.response || error?.message || '';
            const isThrottlingError = errorMessageText.includes('throttlingException') || 
                                      errorMessageText.includes('throttling') ||
                                      errorMessageText.includes('request rate is too high');
            
            if (isThrottlingError && currentAttempt < 2) {
              // Still throttling, retry again
              const nextAttempt = currentAttempt + 1;
              console.log(`Retry attempt ${currentAttempt + 1} failed with throttling, will retry again (attempt ${nextAttempt + 1} of 3)...`);
              setRetryAttempt(nextAttempt);
              setRetryCountdown(30);
              
              const retryMessage = {
                role: 'assistant',
                content: `I am going to retry in 30 seconds... (attempt ${nextAttempt + 1} of 3)`,
                timestamp: new Date().toISOString(),
                isRetryMessage: true
              };
              
              setMessages(prev => {
                const filtered = prev.filter(msg => 
                  !(msg.role === 'assistant' && msg.content && msg.content.includes('I am going to retry in'))
                );
                const next = [...filtered, retryMessage];
                sessionService.saveMessages(sessionId, next);
                return next;
              });
              setLoading(false);
              return;
            }
            
            // Non-throttling error or max retries reached
            const errorMessage = {
              role: 'assistant',
              content: currentAttempt >= 2 
                ? 'Sorry, I encountered throttling errors after 3 retry attempts. Please wait a moment and try again.'
                : 'Sorry, I encountered an error on retry. Please try again.',
              timestamp: new Date().toISOString()
            };
            setMessages(prev => {
              const filtered = prev.filter(msg => 
                !(msg.role === 'assistant' && msg.content && msg.content.includes('I am going to retry in'))
              );
              const next = [...filtered, errorMessage];
              sessionService.saveMessages(sessionId, next);
              return next;
            });
            
            // Reset retry state
            setRetryAttempt(0);
            setPendingRetryMessage(null);
            setRetryCountdown(null);
          } finally {
            setLoading(false);
          }
        };
        
        retryFunction();
      }
    }
  }, [retryCountdown, pendingRetryMessage, sessionId, onNewMessage, retryAttempt]);

  // Load messages when session ID changes (user switched threads or created new chat)
  useEffect(() => {
    if (!sessionId) {
      setMessages([]);
      return;
    }
    
    console.log('ChatInterface: Loading messages for session:', sessionId);
    
    // Clear any pending retry when switching sessions
    setPendingRetryMessage(null);
    setRetryCountdown(null);
    setRetryAttempt(0);
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    
    const loadMessagesForSession = async (sessionIdToLoad) => {
      // Prefer local storage first for chat history
      const localMsgs = sessionService.getMessages(sessionIdToLoad);
      if (localMsgs && localMsgs.length > 0) {
        setMessages(localMsgs);
        return;
      }
      // Fallback to backend if nothing in local storage (e.g., fresh browser)
      try {
        const sessionData = await chatService.getSession(sessionIdToLoad);
        if (sessionData && sessionData.messages && sessionData.messages.length > 0) {
          const formattedMessages = sessionData.messages.map(msg => ({
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp || new Date().toISOString()
          }));
          setMessages(formattedMessages);
          sessionService.saveMessages(sessionIdToLoad, formattedMessages);
        } else {
          setMessages([]);
        }
      } catch (error) {
        console.error('Error loading messages for session:', error);
        setMessages([]);
      }
    };
    
    // Load messages when session changes
    loadMessagesForSession(sessionId);
  }, [sessionId]);

  const handleSendMessage = async (message, isRetry = false) => {
    if (!sessionId) {
      console.error('No session ID available');
      return;
    }
    
    console.log('Sending message with session ID:', sessionId, isRetry ? '(retry)' : '');
    
    // Only add user message if this is not a retry (user message already added)
    if (!isRetry) {
      const userMessage = {
        role: 'user',
        content: message,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => {
        const next = [...prev, userMessage];
        sessionService.saveMessages(sessionId, next);
        // Notify parent to update thread title if this is the first message
        if (prev.length === 0 && onNewMessage) {
          onNewMessage();
        }
        return next;
      });
    }
    
    setLoading(true);

    // Clear any existing retry timer
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    // Only reset retry state if this is a new message (not a retry)
    if (!isRetry) {
      setRetryCountdown(null);
      setPendingRetryMessage(null);
      setRetryAttempt(0);
    }

    try {
      const response = await chatService.sendMessage(sessionId, message);
      
      // Check if backend returned an error (backend returns error as successful response with error: true)
      if (response.error || (response.response && response.response.includes('encountered an error'))) {
        // Extract error message from response
        const errorMessageText = response.response || '';
        console.log('Error response detected:', errorMessageText);
        
        // Check if this is a throttling exception
        const isThrottlingError = errorMessageText.includes('throttlingException') || 
                                  errorMessageText.includes('throttling') ||
                                  errorMessageText.includes('request rate is too high');
        
        console.log('Is throttling error?', isThrottlingError);
        
        if (isThrottlingError) {
          // Set up retry in 30 seconds (attempt 1 of 3)
          const currentAttempt = retryAttempt + 1;
          setPendingRetryMessage(message);
          setRetryCountdown(30);
          setRetryAttempt(0); // Reset to 0 for first attempt
          
          const retryMessage = {
            role: 'assistant',
            content: `I am going to retry in 30 seconds... (attempt 1 of 3)`,
            timestamp: new Date().toISOString(),
            isRetryMessage: true
          };
          
          setMessages(prev => {
            const next = [...prev, retryMessage];
            sessionService.saveMessages(sessionId, next);
            return next;
          });
          setLoading(false);
          return;
        } else {
          // Regular error - show error message
          const errorMessage = {
            role: 'assistant',
            content: errorMessageText || 'Sorry, I encountered an error. Please try again.',
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, errorMessage]);
          setLoading(false);
          return;
        }
      }
      
      // Check if PDF is available
      if (response.pdf_available) {
        // PDF is available - show message with PDF icon
        const pdfMessage = {
          role: 'assistant',
          content: response.response || 'Your campaign plan PDF is ready!',
          timestamp: new Date().toISOString(),
          pdf_blob_url: response.pdf_blob_url,
          pdf_filename: response.pdf_filename
        };
        
        setMessages(prev => {
          const filtered = prev.filter(msg => 
            !(msg.role === 'assistant' && msg.content && msg.content.includes('I am going to retry in'))
          );
          const next = [...filtered, pdfMessage];
          sessionService.saveMessages(sessionId, next);
          return next;
        });
        
        if (onNewMessage) {
          onNewMessage();
        }
        setLoading(false);
        return;
      }
      
      // The frontend generates and maintains the session ID
      // Backend should accept it and return the same one
      // If backend returns a different ID, we'll still use the frontend's ID for consistency
      if (response.session_id && response.session_id !== sessionId) {
        console.warn('Backend returned different session ID:', sessionId, '->', response.session_id);
        // Keep using the frontend-generated session ID to maintain conversation context
        console.log('Maintaining frontend session ID:', sessionId);
      }
      
      const assistantMessage = {
        role: 'assistant',
        content: response.response,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => {
        // If this was a retry, remove any pending retry message from the list
        const filtered = prev.filter(msg => 
          !(msg.role === 'assistant' && msg.content && msg.content.includes('I am going to retry in'))
        );
        const next = [...filtered, assistantMessage];
        sessionService.saveMessages(sessionId, next);
        return next;
      });
      
      if (onNewMessage) {
        onNewMessage();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Check if this is a throttling exception (for actual HTTP errors)
      const errorMessage = error?.response?.data?.error || error?.response?.data?.response || error?.message || '';
      const isThrottlingError = errorMessage.includes('throttlingException') || 
                                errorMessage.includes('throttling') ||
                                errorMessage.includes('request rate is too high');
      
      if (isThrottlingError) {
        // Set up retry in 30 seconds
        setPendingRetryMessage(message);
        setRetryCountdown(30);
        
        const retryMessage = {
          role: 'assistant',
          content: `I am going to retry in 30 seconds...`,
          timestamp: new Date().toISOString(),
          isRetryMessage: true
        };
        
        setMessages(prev => {
          const next = [...prev, retryMessage];
          sessionService.saveMessages(sessionId, next);
          return next;
        });
      } else {
        // Regular error
        const errorMessage = {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="chat-interface">
      <MessageList messages={messages} loading={loading} />
      <InputBox onSubmit={handleSendMessage} disabled={loading} />
    </div>
  );
}

